<template>
  <v-card elevation="0" class="mb-6">
    <v-card-title class="d-flex align-center justify-space-between px-0">
      <div class="font-weight-bold">{{ $t("Lots Traceability") }}</div>
    </v-card-title>

    <v-card-text class="px-0">
      <div class="d-flex align-center flex-wrap" style="gap: 12px;">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          :placeholder="$t('search')"
          style="max-width: 280px;"
          clearable
          @input="debouncedFetch"
        />

        <v-autocomplete
          v-model="processorFilter"
          :items="processorOptions"
          item-text="text"
          item-value="val"
          outlined
          dense
          hide-details
          clearable
          :label="$t('processor')"
          style="min-width: 260px;"
        />

        <v-spacer />

        <div class="d-flex align-center" style="gap: 8px;">
          <div class="text-body-2">
            {{ from }} - {{ to }} of {{ totalCount }}
          </div>
          <v-btn fab small outlined color="primary" :disabled="options.page <= 1" @click="pageChange(false)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            fab
            small
            outlined
            color="primary"
            :disabled="options.page >= totalPages"
            @click="pageChange(true)"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <v-skeleton-loader v-if="loading" class="mt-4" type="table" />

      <v-expansion-panels
        v-else
        class="mt-4 lot-panels"
        v-model="openLotPanel"
        accordion
      >
        <v-expansion-panel v-for="lot in pagedItems" :key="lot.key">
          <v-expansion-panel-header class="lot-header">
            <div class="d-flex align-center justify-space-between" style="width: 100%;">
              <span class="font-weight-medium">{{ lot.lotId }}</span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Inner traceability details -->
            <LotsTraceability :lot="lot.original" />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div v-if="!loading && pagedItems.length === 0" class="text-center text--secondary mt-6">
        {{ $t("No lots found") }}
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { debounce } from "lodash";
import CoffeeService from "@/_services/CoffeeService";
import CacaoDataService from "@/_services/CacaoDataService";
import LotsTraceability from "./LotsTraceability.vue";

export default {
  name: "LotsTraceabilityList",
  components: { LotsTraceability },
  props: {
    sourceType: {
      type: String,
      required: true,
      validator: (v) => ["coffee", "cacao"].includes(v),
    },
    // Expand first lot by default (matches screenshot)
    openFirstByDefault: {
      type: Boolean,
      default: true,
    },
    productionPlaceLotInfo: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      search: "",
      processorFilter: null,
      processorOptions: [],
      rawItems: [],
      totalCount: 0,
      from: 0,
      to: 0,
      openLotPanel: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
    };
  },
  computed: {
    filteredItems() {
      const proc = this.processorFilter;
      if (!proc) return this.rawItems;
      return this.rawItems.filter((x) => (x.processor || "").toLowerCase() === proc.toLowerCase());
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.options.itemsPerPage));
    },
    pagedItems() {
      const startIndex = (this.options.page - 1) * this.options.itemsPerPage;
      const endIndex = startIndex + this.options.itemsPerPage;
      return this.filteredItems.slice(startIndex, endIndex);
    },
  },
  watch: {
    sourceType: {
      immediate: true,
      handler() {
        this.options.page = 1;
        this.search = "";
        this.processorFilter = null;
        this.openLotPanel = 0;
        this.fetchLots();
      },
    },
    processorFilter() {
      this.options.page = 1;
      this.openLotPanel = 0;
      this.updatePager();
    },
    pagedItems() {
      this.updatePager();
      // If we have items and want default open, ensure first panel is opened.
      if (this.openFirstByDefault && this.pagedItems.length > 0 && (this.openLotPanel == null || this.openLotPanel < 0)) {
        this.openLotPanel = 0;
      }
    },
  },
  created() {
    this.debouncedFetch = debounce(() => {
      this.options.page = 1;
      this.openLotPanel = 0;
      this.fetchLots();
    }, 500);
  },
  methods: {
    pageChange(next) {
      this.options.page = next ? this.options.page + 1 : this.options.page - 1;
      this.openLotPanel = 0;
      this.updatePager();
    },
    updatePager() {
      this.totalCount = this.filteredItems.length;
      const start = (this.options.page - 1) * this.options.itemsPerPage;
      const end = start + this.pagedItems.length;
      this.from = this.totalCount === 0 ? 0 : start + 1;
      this.to = end;
      if (this.options.page > this.totalPages) this.options.page = this.totalPages;
    },
    extractProcessorName(item) {
      const tryPaths = [
        item?.processingBatch?.[0]?.dryMilling?.fullName,
        item?.parchmentCoffeeProcessingBatches?.[0]?.parchmentCoffee?.dryMilling?.fullName,
        item?.buyingStationOrder?.[0]?.buyingStation?.fullName,
        item?.cacaoPurchaseOrder?.[0]?.buyingStation?.fullName,
      ];
      return tryPaths.find(Boolean) || "N/A";
    },
    mapLotRow(item) {
      if (this.sourceType === "coffee") {
        const lotId = item.batchCode || "N/A";
        return {
          key: `lot-${lotId}-${item.id ?? ""}`,
          lotId,
          processor: this.extractProcessorName(item),
          original: { ...item, type: "coffee" },
        };
      }
      const lotId = item.fermentationCode || "N/A";
      return {
        key: `lot-${lotId}-${item.id ?? ""}`,
        lotId,
        processor: this.extractProcessorName(item),
        original: { ...item, type: "cacao" },
      };
    },
    buildProcessorOptions() {
      const set = new Set();
      this.rawItems.forEach((x) => {
        if (x.processor && x.processor !== "N/A") set.add(x.processor);
      });
      this.processorOptions = Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((p) => ({ text: p, val: p }));
    },
    lotMatchesProductionPlace(lot) {
      // Check if this lot matches any production place lot information
      if (!this.productionPlaceLotInfo || this.productionPlaceLotInfo.length === 0) {
        return true; // If no filter, show all lots
      }

      const lotId = this.sourceType === "coffee" ? lot.batchCode : lot.fermentationCode;
      const lotItemId = lot.id;

      // Match by sourceType, lotId (batchCode/fermentationCode), and lotItemId
      return this.productionPlaceLotInfo.some(ppLot =>
        ppLot.sourceType === this.sourceType &&
        ppLot.lotId === lotId &&
        ppLot.lotItemId === Number(lotItemId)
      );
    },
    async fetchLots() {
      this.loading = true;
      try {
        const options = { page: 1, itemsPerPage: 500 };
        const serviceRes =
          this.sourceType === "coffee"
            ? await CoffeeService.getCoffeeData(options, this.search)
            : await CacaoDataService.getCacaoData(options, this.search);

        const ok = serviceRes?.code === 200;
        const response = ok ? serviceRes?.data?.response : [];
        const list = Array.isArray(response) ? response : [];

        // Filter lots to only include those related to production places
        const filteredList = this.productionPlaceLotInfo && this.productionPlaceLotInfo.length > 0
          ? list.filter(lot => this.lotMatchesProductionPlace(lot))
          : list;

        this.rawItems = filteredList.map((i) => this.mapLotRow(i));
        this.buildProcessorOptions();
        this.updatePager();

        // Always open the first lot (matches screenshot)
        this.openLotPanel = this.openFirstByDefault && this.pagedItems.length > 0 ? 0 : null;
      } catch (e) {
        this.rawItems = [];
        this.processorOptions = [];
        this.totalCount = 0;
        this.from = 0;
        this.to = 0;
        this.openLotPanel = null;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
::v-deep .lot-panels .v-expansion-panel {
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  margin-bottom: 10px;
}

::v-deep .lot-panels .v-expansion-panel::before {
  box-shadow: none;
}

::v-deep .lot-header {
  background: #efefef;
  min-height: 46px;
}
</style>




